import {
    animate, state, style, transition, trigger,
} from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
    Component,
    ContentChildren,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import {
    MatColumnDef, MatTable, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell,
    MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import {
    debounceTime, merge, startWith, Subject,
} from 'rxjs';
import { BreakpointService } from '../../../services/breakpoint.service';
import { NgIf, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';

function CustomPaginator(): MatPaginatorIntl {
    const customPaginatorIntl = new MatPaginatorIntl();
    customPaginatorIntl.firstPageLabel = '';
    customPaginatorIntl.lastPageLabel = '';
    customPaginatorIntl.nextPageLabel = '';
    customPaginatorIntl.previousPageLabel = '';
    customPaginatorIntl.itemsPerPageLabel = $localize`Aantal rijen per pagina`;
    customPaginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number): string => {
        if (length === 0 || pageSize === 0) {
            return $localize`0 van de ` + length;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
        return `${startIndex + 1}-${endIndex}${$localize` van de `}${length}`;
    };
    return customPaginatorIntl;
}

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useValue: CustomPaginator() }],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        NgIf,
        MatFormField,
        MatIcon,
        MatPrefix,
        MatInput,
        ReactiveFormsModule,
        MatIconButton,
        MatTable,
        NgClass,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCheckbox,
        MatCellDef,
        MatCell,
        NgTemplateOutlet,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatNoDataRow,
        MatPaginator,
    ],
})
export class TableComponent {
    /**
     * Reference to material table
     */
    @ViewChild(MatTable, { static: true }) table?: MatTable<any>;

    /**
     * Paginator
     */
    @ViewChild(MatPaginator) paginator?: MatPaginator;

    /**
     * Reference to material column defintions that are content-projected into this component
     */
    @ContentChildren(MatColumnDef) columnDefs?: QueryList<MatColumnDef>;

    /**
     * Table settings
     */
    @Input() settings?: TableSettings;

    /**
     * Reference to template from parent component for the expanded details
     */
    @Input() expandedTemplate: TemplateRef<any> | null = null;

    /**
     * Outputs an event when the user pressed the delete button on a row.
     * The settings.enableRowDeletion property must be set to true.
     */
    @Output() rowDeleted = new EventEmitter<any>();

    /**
     * Destroy ref used in takeUntilDestroyed
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Filtered and sorted items to be displayed in table
     */
    filteredItems: GenericItem[] = [];

    /**
     * The row that is expanded if possible.
     */
    expandedItem?: GenericItem;

    /**
     * Columns to display in the table
     * Variable is set in onChanges
     */
    displayedColumns: string[] = [];

    /**
     * Selection model to keep track of selected items
     */
    selection = new SelectionModel<string>(true, []);

    /**
     * Contains currently selected items
     */
    selectedItems: GenericItem[] = [];

    /**
     * Current where clause used in search query and selectAll query
     */
    currentWhere: any;

    /**
     * Control used to search and filter items
     */
    searchControl = new FormControl('');

    /**
     * Send a new value through this object to trigger a reload of the data
     */
    updateData = new Subject<void>();

    /**
     * Tracks whether data is loaded at least once to avoid flickering of data
     */
    initialLoadCompleted = false;

    constructor(public breakpoint: BreakpointService, private router: Router) {
        // Subscribe to selection changes and update selectedItems array to contain all items that are selected
        this.selection.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.selectedItems = this.filteredItems.filter((item) => this.selection.selected.includes(item.id));
        });
    }

    /**
     * After ng-content has been initialized, the column defintions are ready to be loaded into the material table
     */
    ngAfterContentInit(): void {
        this.columnDefs?.forEach((columnDef) => {
            if (columnDef.name !== 'expandedDetail') {
                this.displayedColumns.push(columnDef.name);
                this.table?.addColumnDef(columnDef);
            }
        });
        // Add delete column if required
        if (this.settings?.enableRowDeletion) {
            this.displayedColumns.push('delete');
        }

        // Add expand column if required
        if (this.settings?.enableExpansion) {
            this.displayedColumns.push('expand');
        }
    }

    /**
     * Handle input changes
     * Called before any other lifecycle hook such as ngAfterContentInit
     */
    ngOnChanges(): void {
        // Add checkbox column if required
        if (this.settings?.enableSelection) {
            this.displayedColumns = ['checkbox'];
        }
    }

    /**
     * Subscribe to paginator and search changes and update data to be displayed in table
     */
    ngAfterViewInit(): void {
        if (this.paginator) {
            merge(
                this.paginator.page.asObservable(),
                this.searchControl.valueChanges.pipe(debounceTime(300)),
                this.updateData,
            ).pipe(startWith(null)).subscribe(async () => {
                if (this.paginator && this.settings?.getItems) {
                    const data = await this.settings.getItems(this.paginator, this.searchControl.value || '');
                    this.initialLoadCompleted = true;
                    this.filteredItems = data.data[Object.keys(data.data)[0]];
                    this.paginator.length = data.data[Object.keys(data.data)[1]].aggregate.count;
                }
            });
        }
    }

    /**
     * Toggle all the selection checkboxes on the current page
     * @param change Mat checkbox change
     */
    toggleAllRows(change: MatCheckboxChange): void {
        if (change.source.checked) {
            this.selection.select(...this.filteredItems.map((option) => option.id));
        } else {
            this.selection.deselect(...this.filteredItems.map((option) => option.id));
        }
    }

    /**
     * Checks if the main checkbox should be in checked state
     * @returns True if main checkbox should be in checked state, false otherwise
     */
    isMainChecked(): boolean {
        return !!this.filteredItems.length && this.filteredItems.every((option) => this.selection.isSelected(option.id));
    }

    /**
     * Checks if the main checkbox should be in intermediate state
     * @returns True if main checkbox should be in intermediate state, false otherwise
     */
    isMainIntermediate(): boolean {
        return !!this.filteredItems.length &&
            !this.isMainChecked() && this.filteredItems.some((option) => this.selection.isSelected(option.id));
    }

    /**
     * Checks if the select all button should be shown. Only true if everything on the page is selected and not all items.
     * @returns True if select all text should be shown, false otherwise
     */
    showSelectAll(): boolean {
        return this.isMainChecked() && !(this.selection.selected.length === this.paginator?.length);
    }

    /**
     * Expands or routes to row
     * @param item Row to route to or expand
     */
    routeOrExpand(item: any): void {
        if (this.settings?.routerLinkFunction) {
            this.router.navigateByUrl(this.settings.routerLinkFunction(item));
        } else if (this.settings?.enableExpansion) {
            // if no route and expansion is enabled, set the expandedItem to expand it
            this.expandedItem = this.expandedItem === item ? null : item;
        }
    }
}

/**
 * Table settings
 */
export interface TableSettings {
    /**
     * Function that returns items to be displayed in table in combination with an aggregation.
     * Data response should follow this structure:
     * {
     *  data: {
     *      items: any[],
     *      items_aggregate: {aggregate: {count: number}}
     *      }
     * }
     * Where items/items_aggregate can be any key/name. The aggregate must always be the second key in the object
     * @param paginator Paginator instance
     * @param search Current value (string) of searchcontrol
     * @returns Data to be displayed in the table
     */
    getItems: (paginator: MatPaginator, search: string) => Promise<any>;
    /**
     * Set to true to enable selection boxes
     */
    enableSelection?: boolean;
    /**
     * Set to true to show a delete button at the end of the row. The table output a rowDeleted() event with the item that was deleted.
     */
    enableRowDeletion?: boolean;
    /**
     * Set to true to show an expansion icon button at the end of the row. The rows can then be expanded when clicked which will show a
     * column with matColumnDef="expandedDetail"
     */
    enableExpansion?: boolean;
    /**
     * Set to true to hide the paginator
     */
    hidePaginator?: boolean;
    /**
     * Set to true to hide the search input
     */
    hideSearch?: boolean;
    /**
     * Set to true to hide the table header (can be useful with tables with only one column)
     */
    hideHeader?: boolean;
    /**
     * Set to true to hide the page size selection of the paginator
     */
    hidePageSize?: boolean;
    /**
     * Optional parameter to enable clicking table rows. When the row is clicked the routerLink function will
     * determine where the user is send to.
     * @param item Item that is displayed in a row of the table
     * @returns Router link URL to route to on click
     */
    routerLinkFunction?: (item: any) => string;
}

/**
 * Defines generic item type requiring an id to be present
 */
interface GenericItem {
    id: string;
    [key: string]: any;
}
